/**
 * The theater's platform helpers — icon, CSS modifier, and the video-embed and
 * external-link URLs. Chat comes from `getChatEmbedUrl` in `shared.tsx`.
 *
 * Split out of `StreamTheater.tsx` for the 400-line ceiling. Everything here is
 * a pure function of a `StreamData`. The URLs themselves are built by the one
 * shared builder in `shared.tsx`, which is where the sanitisation lives.
 */
import React from "react";
import { FaTwitch, FaYoutube, FaVideo } from "react-icons/fa";
import { SiKick } from "react-icons/si";
import { formatCompactNumber } from "@/utils/formatCompactNumber";
import { getStreamEmbedUrl, getStreamExternalUrl } from "../shared";
import type { StreamData } from "../StreamTheater";

/*
 * A member may have no platform, so the modifier suffix falls back to a neutral
 * value rather than being interpolated from `undefined`.
 */
export const platformClass = (platform?: string): string => platform?.toLowerCase() ?? "unknown";

export const getPlatformIcon = (platform?: string) => {
    switch (platform) {
        case "TWITCH":
            return <FaTwitch />;
        case "YOUTUBE":
            return <FaYoutube />;
        case "KICK":
            return <SiKick />;
        default:
            return <FaVideo />;
    }
};

/* `#` keeps this usable as a bare href when a member has no linkable channel. */
export const getPlatformUrl = (stream: StreamData): string =>
    getStreamExternalUrl(stream) ?? "#";

/*
 * VOD ids are flattened onto the source: they are more specific than the
 * channel, so the shared builder prefers them. Kick has no public VOD embed and
 * falls through to its live-channel player, which renders Kick's own offline
 * screen for a past broadcast — best-effort theatrical UX for that platform.
 */
export const getEmbedUrl = (stream: StreamData, muted: boolean = false): string =>
    getStreamEmbedUrl({
        ...stream,
        twitchVideoId: stream.vod?.twitchVideoId,
        youtubeVideoId: stream.vod?.youtubeVideoId,
    }, {muted});

export const formatViewerCount = (count: number): string => formatCompactNumber(count);
